"use client";

import type { JsonValue } from "@prisma/client/runtime/library";
import { createSlateEditor, type Value } from "platejs";
import * as React from "react";

import { BaseEditorKit } from "@/components/editor/editor-base-kit";
import { EditorStatic } from "@/components/ui/editor-static";

/**
 * Creates a Plate editor instance for rendering JSON content
 * @param content The JSON content to render
 * @param defaultMessage Optional custom message to show when content is empty
 * @returns A configured Plate editor instance
 */
export function createPlateEditorForContent(
  content: JsonValue,
  defaultMessage = "No content has been added yet.",
) {
  // Handle null/undefined content or non-array content by providing a default empty content
  const safeContent =
    content && Array.isArray(content)
      ? content
      : [
          {
            type: "p",
            children: [{ text: defaultMessage }],
          },
        ];

  return createSlateEditor({
    plugins: BaseEditorKit,
    value: safeContent as Value,
  });
}

/**
 * Component to render Plate content
 * @param content The JSON content to render
 * @param defaultMessage Optional custom message to show when content is empty
 * @param variant Optional variant for the EditorStatic component
 */
export function PlateContentRenderer({
  content,
  defaultMessage,
  variant = "none",
}: {
  content: JsonValue;
  defaultMessage?: string;
  variant?:
    | "none"
    | "default"
    | "ai"
    | "aiChat"
    | "demo"
    | "fullWidth"
    | "select";
}) {
  const editor = createPlateEditorForContent(content, defaultMessage);

  return <EditorStatic editor={editor} variant={variant} />;
}
